
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Clock, Calendar } from 'lucide-react';
import { NewsArticle } from '@/types/news';
import { formatDistanceToNow } from 'date-fns';

interface NewsArticleCardProps {
  article: NewsArticle;
  variant?: 'default' | 'compact' | 'featured';
}

const NewsArticleCard: React.FC<NewsArticleCardProps> = ({ 
  article, 
  variant = 'default' 
}) => {
  const { 
    title, 
    description, 
    imageUrl, 
    source, 
    sourceIconUrl, 
    url, 
    publishDate, 
    category,
    readTime 
  } = article;

  if (variant === 'compact') {
    return (
      <Card className="h-full hover:shadow-md transition-shadow duration-200">
        <div className="flex h-full">
          <div className="w-1/3 h-full">
            <img 
              src={imageUrl} 
              alt={title} 
              className="h-full w-full object-cover rounded-l-lg"
            />
          </div>
          <div className="w-2/3 flex flex-col justify-between">
            <CardHeader className="p-3 pb-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="capitalize text-xs">
                  {category}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(publishDate, { addSuffix: true })}
                </span>
              </div>
              <CardTitle className="text-base line-clamp-2">{title}</CardTitle>
            </CardHeader>
            <CardFooter className="p-3 pt-0 text-xs text-muted-foreground">
              <div className="flex justify-between w-full">
                <span>{source}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 -mr-2" 
                  onClick={() => window.open(url, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" /> Read
                </Button>
              </div>
            </CardFooter>
          </div>
        </div>
      </Card>
    );
  }

  if (variant === 'featured') {
    return (
      <Card className="overflow-hidden h-full hover:shadow-md transition-shadow duration-200" variant="soundcharts">
        <div className="relative h-60">
          <img 
            src={imageUrl} 
            alt={title} 
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
            <Badge variant="outline" className="mb-2 bg-white/20 backdrop-blur-sm w-fit capitalize">
              {category}
            </Badge>
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-white/90 text-sm flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {publishDate.toLocaleDateString()}
              </span>
              {readTime && (
                <span className="text-white/90 text-sm flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {readTime} min read
                </span>
              )}
            </div>
          </div>
        </div>
        <CardContent className="p-4">
          <CardDescription className="line-clamp-3 mb-4">{description}</CardDescription>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {sourceIconUrl && (
                <img src={sourceIconUrl} alt={source} className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">{source}</span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => window.open(url, '_blank')}
            >
              Read Article <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default card
  return (
    <Card className="h-full hover:shadow-md transition-shadow duration-200">
      <div className="aspect-video rounded-t-lg overflow-hidden">
        <img 
          src={imageUrl} 
          alt={title} 
          className="h-full w-full object-cover"
        />
      </div>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="capitalize">
            {category}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(publishDate, { addSuffix: true })}
          </span>
        </div>
        <CardTitle className="line-clamp-2">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <CardDescription className="line-clamp-3">{description}</CardDescription>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {sourceIconUrl && (
            <img src={sourceIconUrl} alt={source} className="h-5 w-5" />
          )}
          <span className="text-sm font-medium">{source}</span>
        </div>
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => window.open(url, '_blank')}
        >
          Read Article <ExternalLink className="ml-1 h-3 w-3" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NewsArticleCard;
